import * as React from 'react';

import { escape as escapeHtml } from 'lodash';

import { translate } from '~l10n/client';
import { SmallButton, UIButton } from '~shared/components/buttons';
import { DialogConfirmation } from '~shared/components/Dialog';
import { Spinner } from '~shared/components/Spinner';
import { Search } from '~shared/components/Table/update/components/Search';
import {
  TeacherApplicationContext,
  useTeacherApplicationContext,
} from '~shared/context/TeacherApplicationContext';
import { WorkspaceInviteDialog } from '~teacher/components/workspaces/InviteWorkspaceDialog/InviteWorkspaceDialog';
import { useWorkspaceActions } from '~teacher/components/workspaces/WorkspaceMembers/WorkspaceMembersTable/useWorkspaceActions';
import { useGetWorkspaceDetail } from '~teacher/hook/workspaces';
import { useGetWorkspaceRole } from '~teacher/hook/workspaces/useGetWorkspaceRole';
import { useGetWorkspaceSettings } from '~teacher/hook/workspaces/useGetWorkspaceSettings';
import { useWorkspaceMembers } from '~teacher/hook/workspaces/useWorkspaceMembers';

import {
  MembersSortBy,
  MembersSortDir,
  WorkspaceMember,
} from '../../../../api/workspaces/getWorkspaceMembers';
import { canAccessInvites } from '../../../../api/workspaces/roleMeetsFloor';
import { RemoveMemberDialog } from './RemoveMemberDialog';
import { WorkspaceMembersTable } from './WorkspaceMembersTable';

type MembersSort = { by: MembersSortBy; dir: MembersSortDir } | null;

interface Props {
  workspaceId: string;
}

type WorkspaceMemberTableItem = WorkspaceMember & {
  // When displaying in lists we want to know if this is the current user
  isYou: boolean;
};

/**
 * Hook that returns the workspace members fetched so far (across all
 * pages of the infinite query), tagged with an `isYou` flag, plus the
 * pagination controls needed by the table.
 */
function useWorkspaceMemberTableData({
  workspaceId,
  sort,
  search,
}: {
  workspaceId: string;
  sort: MembersSort;
  search: string;
}) {
  const workspaceMembersQuery = useWorkspaceMembers({
    workspaceId,
    sortBy: sort?.by,
    sortDir: sort?.dir,
    search,
  });
  const userRoleQuery = useGetWorkspaceRole({
    workspaceId,
    select: (workspaceMember) => workspaceMember.id,
  });

  const data = React.useMemo(() => {
    return workspaceMembersQuery.members.map<WorkspaceMemberTableItem>(
      (workspaceMember) => ({
        ...workspaceMember,
        isYou: workspaceMember.id === userRoleQuery.data,
      })
    );
  }, [userRoleQuery.data, workspaceMembersQuery.members]);

  return {
    data: data,
    workspaceMembersQuery,
    requestUser: userRoleQuery,
  };
}

export function WorkspaceMembers({ workspaceId }: Props) {
  const {
    isDisabled,
    tableActions,
    onTransfer,
    onRemove,
    isTransferOpen,
    isRemoveOpen,
    onRemoveClose,
    onTransferClose,
    username,
    isRemovingSelf,
  } = useWorkspaceActions(workspaceId);
  const [showInviteDialog, setShowInviteDialog] = React.useState(false);
  const [sort, setSort] = React.useState<MembersSort>(null);
  const [search, setSearch] = React.useState('');
  // Assessments scrubs emails server-side, so its member search is name-only
  const canSearchEmail =
    useTeacherApplicationContext() !==
    TeacherApplicationContext.ShowbieAssessments;
  const onSortChange = React.useCallback((column: MembersSortBy) => {
    setSort((prev) => {
      if (prev?.by !== column) return { by: column, dir: 'asc' };
      if (prev.dir === 'asc') return { by: column, dir: 'desc' };
      return null;
    });
  }, []);
  const { data, workspaceMembersQuery } = useWorkspaceMemberTableData({
    workspaceId,
    sort,
    search,
  });

  const hasSearch = search.trim().length > 0;

  const isFetchingFirstPage =
    workspaceMembersQuery.isFetching &&
    !workspaceMembersQuery.isFetchingNextPage;

  // Gate the invite affordance on the allow_member_invite role-floor: the
  // current user may only invite if their role meets or exceeds the floor.
  const currentRoleQuery = useGetWorkspaceRole({
    workspaceId,
    select: (workspaceMember) => workspaceMember.role,
  });
  const inviteFloorQuery = useGetWorkspaceSettings({
    workspaceId,
    select: (settings) => settings.allowMemberInvite,
  });
  // Until both the role and the invite-floor resolve we can't know the correct
  // enabled/disabled state, so hide the button until then rather than
  // defaulting to disabled — otherwise editors briefly see a disabled button
  // before it flips to enabled.
  const isInviteGateLoading =
    currentRoleQuery.isLoading || inviteFloorQuery.isLoading;
  const canInvite =
    currentRoleQuery.data != null &&
    inviteFloorQuery.data != null &&
    canAccessInvites(currentRoleQuery.data, inviteFloorQuery.data);

  return (
    <>
      {/* Search fills the row from the list's left edge; Invite stays pinned
          right. Kept above the table so it persists even when a search
          returns no matches (the table hides its children when empty). */}
      <div className="flex items-center gap-3 mt-5 mb-5">
        <div className="flex-1">
          <Search
            setQuery={setSearch}
            placeholder={
              canSearchEmail
                ? translate('Search members by name or email')
                : translate('Search members by name')
            }
          />
        </div>
        {!isInviteGateLoading && (
          <SmallButton
            appearance={'primary'}
            className="whitespace-no-wrap"
            isDisabled={!canInvite}
            onClick={() => setShowInviteDialog(true)}
          >
            {translate('Invite')}
          </SmallButton>
        )}
      </div>
      <WorkspaceMembersTable
        workspaceId={workspaceId}
        actions={tableActions}
        data={data}
        hasNextPage={workspaceMembersQuery.hasNextPage ?? false}
        isFetchingNextPage={workspaceMembersQuery.isFetchingNextPage}
        fetchNextPage={workspaceMembersQuery.fetchNextPage}
        sort={sort}
        onSortChange={onSortChange}
        emptyState={
          isFetchingFirstPage ? (
            <div className="flex justify-center py-8">
              <Spinner size="small" />
            </div>
          ) : hasSearch && !workspaceMembersQuery.isError ? (
            // Suppressed on error: a failed fetch surfaces the connection-error
            // toast (see useWorkspaceMembers), so it must not read as a genuine
            // no-match here.
            <div className="py-8 text-center text-base text-blue-600">
              {translate('No members match "{0}".').format(search.trim())}
            </div>
          ) : (
            <></>
          )
        }
      />
      <WorkspaceInviteDialog
        workspaceId={workspaceId}
        open={showInviteDialog}
        setOpen={setShowInviteDialog}
      />
      <RemoveMemberDialog
        open={isRemoveOpen}
        onClose={onRemoveClose}
        action={onRemove}
        isDisabled={isDisabled}
        workspaceId={workspaceId}
        name={username ?? ''}
        isSelf={isRemovingSelf}
      />
      <TransferDialog
        open={isTransferOpen}
        onClose={onTransferClose}
        action={onTransfer}
        isDisabled={isDisabled}
        workspaceId={workspaceId}
        name={username ?? ''}
      />
    </>
  );
}

const TransferDialog = ({
  open,
  onClose,
  name,
  action,
  isDisabled,
  workspaceId,
}: {
  open: boolean;
  onClose: () => void;
  name: string;
  action: () => void;
  isDisabled: boolean;
  workspaceId: string;
}) => {
  const { data } = useGetWorkspaceDetail({ workspaceId });
  return (
    <DialogConfirmation
      actions={[
        <UIButton key="cancel" isDisabled={isDisabled} onClick={onClose}>
          {translate('Cancel')}
        </UIButton>,
        <UIButton
          key="primary"
          isDisabled={isDisabled}
          appearance="risky"
          onClick={action}
        >
          {translate('Transfer ownership')}
        </UIButton>,
      ]}
      open={open}
      onClose={onClose}
      id={'transfer-ownership-dialog'}
      title={translate('Transfer ownership to {0}?').format(name)}
    >
      <span
        className="text-blue-900"
        dangerouslySetInnerHTML={{
          __html: translate(
            "{0} will become the new owner of {1}. <strong>You'll be changed to Admin</strong> and will no longer be able to transfer ownership, rename or delete the library."
          ).format(escapeHtml(name), escapeHtml(data?.name ?? '')),
        }}
      />
    </DialogConfirmation>
  );
};
